/**
 * V2 save controls — the "Want to go" button on place pages plus the
 * "Add to list…" dialog for custom lists (M8). Save works like Google
 * Maps lists per the familiarity rules: one tap saves to the system
 * list, the dialog handles everything else.
 *
 * Renders as HTML strings and handles clicks through the host view's
 * existing click delegation (handleSaveClick), same shape as ui/share.js.
 */
import { escapeHtml } from '../../../assets/js/utils.js';
import { icon } from '../../ui/icons.js';
import { showToast } from '../../ui/toast.js';
import {
  WANT_TO_GO,
  isSaved,
  saveWantToGo,
  unsaveWantToGo,
  fetchMyLists,
  fetchMembership,
  createList,
  addToList,
  removeFromList,
} from './lists.js';

/** The save controls for a place. Empty in read-only mode or when the schema isn't live yet. */
export function saveControls(store, locationId) {
  const state = store.get();
  if (state.isReadOnly || state.listsUnavailable) return '';
  const saved = isSaved(store, locationId);
  return `
    <div class="place-save">
      <button type="button" class="v2-btn-save${saved ? ' is-saved' : ''}" data-save-wtg
        aria-pressed="${saved}">
        ${icon('bookmark', 16)}<span>${saved ? 'Saved · Want to go' : 'Want to go'}</span>
      </button>
      ${state.user ? `
      <button type="button" class="v2-btn-more" data-save-list>Add to list…</button>
      <dialog class="v2-auth-dialog list-save-dialog" data-list-dialog>
        <h2 class="v2-dialog-title">Save to list</h2>
        <div data-list-rows><p class="v2-loading">Fetching your lists…</p></div>
        <div class="list-new-row">
          <input type="text" data-new-list-title aria-label="New list name"
            placeholder="New list name" maxlength="80">
          <button type="button" class="v2-btn-more" data-new-list-create>Create</button>
        </div>
        <button type="button" class="v2-btn-dialog-close" data-list-dialog-close>Done</button>
      </dialog>` : ''}
    </div>`;
}

function listRow(list, memberIds) {
  const checked = memberIds.includes(list.id);
  return `
    <label class="list-row">
      <input type="checkbox" data-list-toggle="${list.id}" ${checked ? 'checked' : ''}>
      <span>${escapeHtml(list.title)}</span>
      <span class="list-row-count">${list.entry_count ?? 0}</span>
    </label>`;
}

async function fillDialog(dialog, store, locationId) {
  const rows = dialog.querySelector('[data-list-rows]');
  if (!rows) return;
  try {
    const custom = (await fetchMyLists(store)).filter((l) => l.kind !== WANT_TO_GO.kind);
    if (!custom.length) {
      rows.innerHTML = '<p class="v2-empty">No lists yet — make one below.</p>';
      return;
    }
    const memberIds = await fetchMembership(locationId, custom.map((l) => l.id));
    rows.innerHTML = custom.map((l) => listRow(l, memberIds)).join('');
  } catch {
    rows.innerHTML = '<p class="v2-empty">Couldn’t fetch your lists.</p>';
  }
}

/**
 * Handle a click that may be on the save controls.
 * @returns {boolean} true if the click was a save action
 */
export function handleSaveClick(event, { store, locationId, el }) {
  const wtgButton = event.target.closest('[data-save-wtg]');
  if (wtgButton) {
    toggleWantToGo(store, locationId, wtgButton);
    return true;
  }
  const dialog = el.querySelector('[data-list-dialog]');
  if (event.target.closest('[data-save-list]')) {
    if (dialog) {
      dialog.showModal();
      fillDialog(dialog, store, locationId);
    }
    return true;
  }
  if (event.target.closest('[data-list-dialog-close]')) {
    dialog?.close();
    return true;
  }
  const toggle = event.target.closest('[data-list-toggle]');
  if (toggle) {
    toggleMembership(toggle, locationId);
    return true;
  }
  if (event.target.closest('[data-new-list-create]')) {
    createAndAdd(store, locationId, dialog);
    return true;
  }
  return false;
}

async function toggleWantToGo(store, locationId, button) {
  if (!store.get().user) {
    showToast('Sign in to save places you want to visit.');
    return;
  }
  button.disabled = true;
  try {
    if (isSaved(store, locationId)) {
      await unsaveWantToGo(store, locationId);
    } else {
      await saveWantToGo(store, locationId);
    }
    // The store change re-renders the host view with the new state
  } catch (err) {
    showToast(err?.message || 'Couldn’t save just now — try again.');
  } finally {
    if (button.isConnected) button.disabled = false;
  }
}

async function toggleMembership(checkbox, locationId) {
  const listId = checkbox.dataset.listToggle;
  const adding = checkbox.checked;
  checkbox.disabled = true;
  try {
    if (adding) await addToList(listId, locationId);
    else await removeFromList(listId, locationId);
  } catch {
    checkbox.checked = !adding;
    showToast('Couldn’t update that list — try again.');
  } finally {
    if (checkbox.isConnected) checkbox.disabled = false;
  }
}

async function createAndAdd(store, locationId, dialog) {
  const input = dialog?.querySelector('[data-new-list-title]');
  const title = input?.value.trim();
  if (!title) return;
  try {
    const list = await createList(store, { title });
    if (list) await addToList(list.id, locationId);
    if (input) input.value = '';
    await fillDialog(dialog, store, locationId);
  } catch (err) {
    showToast(err?.message || 'Couldn’t create that list.');
  }
}
